import textConvertor from './textConvertor.js';
import PigeonDocNodeModel from '../../models/PigeonDocNodeModel';

export default class {
  constructor(chart) {
    this.chart = chart;
  }

  async convert(node, options) {
    const chart = this.chart;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', options.width);

    if (options.defaultStyle.font) {
      svg.setAttribute('font-family', options.defaultStyle.font);
    }

    if (options.defaultStyle.fontSize) {
      svg.setAttribute('font-size', options.defaultStyle.fontSize);
    }

    document.body.appendChild(svg);

    if (options.chartOptions.isTextThemeTable) {
      const newData = await generatePosNegData(node['respondent_sentiment']);
      await chart.create(svg, newData, options.chartOptions);
    } else {
      await chart.create(svg, node.content.data, options.chartOptions);
    }

    if (chart.applyStyles) {
      const stylesToApply = ['fill', 'stroke', 'font-size', 'font-family', 'visibility'];
      function stylesReducer(styles) {
        return stylesToApply.reduce((acc, curr) => {
          acc[curr] = styles.getPropertyValue(curr);
          return acc;
        }, {});
      }
      const styles = window.getComputedStyle(svg);
      chart.applyStyles(svg, stylesReducer(styles));

      svg.querySelectorAll('*').forEach((el) => {
        const styles = window.getComputedStyle(el);
        chart.applyStyles(el, stylesReducer(styles));
      });
    }

    /**
     * if the height of the svg exceeds the document height, create a
     * viewbox so that svg scales within the document height
     */
    const svgHeight = svg.getAttribute('height');
    if (svgHeight > options.height) {
      svg.setAttribute('height', options.height);
      const viewbox = `0 0 ${svg.getAttribute('width')} ${svgHeight}`;
      svg.setAttribute('viewBox', viewbox);
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }

    svg.remove();

    let content = {
      stack: [],
    };
    if (node.meta && node.meta.pdfConfig) content = { ...content, ...node.meta.pdfConfig };
    if (node.content && node.content.title.length > 0) {
      const title = textConvertor(node.content.title);
      content.stack = [...title[Object.keys(title)[0]], ...content.stack];
    }

    content.stack.push({
      svg: svg.outerHTML,
      width: parseFloat(svg.getAttribute('width')),
      height: parseFloat(svg.getAttribute('height')),
    });

    return content;
  }
}

function generatePosNegData(data) {
  if (!data || !Array.isArray(data)) data = [];

  const totals = calcRespondentSentimentTotals(data);
  const description =
    `The chart shows sentiments for each respondent in this theme. ` +
    `It shows ${roundToTwo(totals.neg)} ` +
    `responses have a negative sentiment, ${roundToTwo(totals.neu)} ` +
    `are netural, and ${roundToTwo(totals.pos)} are positive.`;

  return new PigeonDocNodeModel({
    type: 'positiveNegativeBarChartSmall',
    content: {
      data: data,
      meta: {
        veryPositiveColour: 'var(--very-positive-soft)',
        positiveColour: 'var(--somewhat-positive-soft)',
        neutralColour: 'var(--neutral-soft)',
        negativeColour: 'var(--somewhat-negative-soft)',
        veryNegativeColour: 'var(--very-negative-soft)',
      },
    },
    meta: {
      description: description,
    },
  });
}

function roundToTwo(num) {
  return +(Math.round(num + 'e+2') + 'e-2');
}

function calcRespondentSentimentTotals(data) {
  return data.reduce(
    (totals, data) => {
      if (data > -0.05 && data < 0.05) totals.neu++;
      if (data >= 0.05) totals.pos++;
      if (data <= -0.05) totals.neg++;
      return totals;
    },
    { neu: 0, pos: 0, neg: 0 },
  );
}
